import { SubscriptionType } from '@/types/shared';
import { GPT_IMAGE_MODEL_TYPES } from '@/consts/gpt-image.consts';
import { FLUX_3_MAX_VARIANT, FLUX_3_VIDEO_MODEL } from '@/components/Nodes/ModelComponents/consts/flux-3.const';
import {
  NANO_BANANA_2_MODEL,
  NANO_BANANA_21_ONLY_PARAMS,
  NANO_BANANA_21_UNSUPPORTED_RESOLUTIONS,
  NANO_BANANA_21_VARIANT,
} from '@/components/Nodes/ModelComponents/consts/nano-banana-2.const';
import { SEEDANCE_25_MODELS } from '@/components/Nodes/ModelComponents/consts/seedance-25.const';
import type { ModelType } from '@/enums/model-type.enum';

/**
 * Model rules the generate page reads, keyed by provider model name. The canvas still applies its own copy in
 * `DynamicFields` and should move onto these, so a rule changes in one place.
 */

/** Models whose duration can be `auto`. */
export const AUTO_DURATION_MODELS: ReadonlySet<string> = new Set([...SEEDANCE_25_MODELS, FLUX_3_VIDEO_MODEL]);

/** Flags that decide which model versions a user can run. */
export type ModelVariantFlags = { isNanoBanana21Enabled: boolean };

/** False for a version behind a flag that is off, which the server rejects. */
export function isModelVariantAllowed(
  modelName: string,
  variant: string | undefined,
  { isNanoBanana21Enabled }: ModelVariantFlags,
): boolean {
  return !(modelName === NANO_BANANA_2_MODEL && variant === NANO_BANANA_21_VARIANT && !isNanoBanana21Enabled);
}

/** Settings that only another version of the model takes. */
export function settingsHiddenForVariant(modelName: string, variant: string | undefined): readonly string[] {
  return modelName === NANO_BANANA_2_MODEL && variant !== NANO_BANANA_21_VARIANT ? NANO_BANANA_21_ONLY_PARAMS : [];
}

/** The options of `key` that this version of the model accepts. */
export function optionsForVariant(
  modelName: string,
  variant: string | undefined,
  key: string,
  options: readonly string[],
): readonly string[] {
  if (modelName === NANO_BANANA_2_MODEL && variant === NANO_BANANA_21_VARIANT && key === 'resolution') {
    return options.filter((option) => !NANO_BANANA_21_UNSUPPORTED_RESOLUTIONS.includes(option));
  }
  return options;
}

/** GPT Image's Auto quality is a paid option. A plan that is still loading keeps it. */
export function canUseAutoQuality(modelName: string | undefined, subscriptionType: SubscriptionType | undefined) {
  const isGptImage = modelName !== undefined && GPT_IMAGE_MODEL_TYPES.has(modelName as ModelType);
  return !isGptImage || subscriptionType !== SubscriptionType.Free;
}

/**
 * Auto can't fit a video to a first or last frame, Flux 3 Max doesn't take it, and Seedance 2.5 takes it only on
 * Enterprise.
 */
export function canUseAutoDuration({
  modelName,
  variant,
  subscriptionType,
  hasKeyframe,
}: {
  modelName: string;
  variant: string | undefined;
  subscriptionType: SubscriptionType | undefined;
  /** A first or last frame is attached. */
  hasKeyframe: boolean;
}): boolean {
  if (!AUTO_DURATION_MODELS.has(modelName) || hasKeyframe) {
    return false;
  }
  if (modelName === FLUX_3_VIDEO_MODEL && variant === FLUX_3_MAX_VARIANT) {
    return false;
  }
  return !SEEDANCE_25_MODELS.has(modelName) || subscriptionType === SubscriptionType.Enterprise;
}
